import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthContext';
import {
  Calendar, MapPin, Clock, Plus, CheckCircle, XCircle,
  Edit2, Trash2, Briefcase, X, Save
} from 'lucide-react';
import { format } from 'date-fns';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({ iconUrl: icon, shadowUrl: iconShadow, iconSize: [25, 41], iconAnchor: [12, 41] });
L.Marker.prototype.options.icon = DefaultIcon;

export default function Interviews() {
  const { activeUserId } = useAuth();
  const [interviews, setInterviews] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingInterview, setEditingInterview] = useState<any>(null);
  const [formData, setFormData] = useState({
    campaign_id: '',
    venue: '',
    address: '',
    date: '',
    notes: '',
    location_lat: '',
    location_lng: '',
  });

  useEffect(() => {
    if (activeUserId) { fetchInterviews(); fetchCampaigns(); }
  }, [activeUserId]);

  const fetchCampaigns = async () => {
    const { data } = await supabase
      .from('campaigns')
      .select('id, title')
      .eq('recruiter_id', activeUserId!);
    setCampaigns(data || []);
  };

  const fetchInterviews = async () => {
    try {
      const { data, error } = await supabase
        .from('interviews')
        .select(`
          *,
          campaign:campaigns(id, title, company_name, recruiter_id)
        `)
        .order('date', { ascending: true });
      if (error) throw error;
      const filtered = (data || []).filter(i => i.campaign?.recruiter_id === activeUserId!);
      setInterviews(filtered);
    } catch (err) {
      console.error('Error fetching interviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingInterview(null);
    setFormData({ campaign_id: '', venue: '', address: '', date: '', notes: '', location_lat: '', location_lng: '' });
    setShowModal(true);
  };

  const openEditModal = (interview: any) => {
    setEditingInterview(interview);
    setFormData({
      campaign_id: interview.campaign_id,
      venue: interview.venue || '',
      address: interview.address || '',
      date: interview.date ? new Date(interview.date).toISOString().split('T')[0] : '',
      notes: interview.notes || '',
      location_lat: interview.location_lat?.toString() || '',
      location_lng: interview.location_lng?.toString() || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        campaign_id: formData.campaign_id,
        venue: formData.venue,
        address: formData.address,
        date: formData.date ? new Date(formData.date).toISOString() : null,
        notes: formData.notes,
        location_lat: formData.location_lat ? parseFloat(formData.location_lat) : null,
        location_lng: formData.location_lng ? parseFloat(formData.location_lng) : null,
        status: 'scheduled',
      };

      if (editingInterview) {
        await supabase.from('interviews').update(payload).eq('id', editingInterview.id);
      } else {
        await supabase.from('interviews').insert(payload);
      }
      setShowModal(false);
      fetchInterviews();
    } catch (err) {
      console.error('Error saving interview:', err);
    }
  };

  const handleStatusChange = async (id: string, status: 'completed' | 'cancelled') => {
    if (!window.confirm(`Are you sure you want to mark this interview as ${status}?`)) return;
    try {
      await supabase.from('interviews').update({ status }).eq('id', id);
      fetchInterviews();
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this interview?')) return;
    try {
      await supabase.from('interviews').delete().eq('id', id);
      fetchInterviews();
    } catch (err) {
      console.error('Error deleting interview:', err);
    }
  };

  const statusColors: Record<string, string> = {
    scheduled: 'bg-blue-50 text-blue-700',
    completed: 'bg-emerald-50 text-emerald-700',
    cancelled: 'bg-red-50 text-red-600',
  };

  const mappedInterviews = interviews.filter(i => i.location_lat && i.location_lng);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-[3px] border-slate-200 border-t-slate-800 rounded-full" />
      </div>
    );
  }

  const inputClass = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-300 focus:bg-white transition-all placeholder:text-slate-400 text-[13px]";

  return (
    <div className="space-y-6 animate-in fade-in" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Interviews</h1>
          <p className="text-slate-400 mt-1 text-[15px]">{interviews.length} sessions scheduled</p>
        </div>
        <button onClick={openAddModal} className="flex items-center gap-2 bg-slate-950 text-white text-[13px] font-semibold px-5 py-2.5 rounded-xl hover:bg-slate-800 transition-colors">
          <Plus size={16} /> Schedule Interview
        </button>
      </div>

      {/* Map View */}
      {mappedInterviews.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
          <div className="h-[300px] relative z-0">
            <MapContainer
              center={[mappedInterviews[0].location_lat, mappedInterviews[0].location_lng]}
              zoom={5}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                attribution='&copy; OpenStreetMap &copy; CARTO'
              />
              {mappedInterviews.map(i => (
                <Marker key={i.id} position={[i.location_lat, i.location_lng]}>
                  <Popup>
                    <strong>{i.venue}</strong><br />
                    {i.campaign?.title}<br />
                    {i.date ? format(new Date(i.date), 'PPP') : 'No date'}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>
      )}

      {/* Interviews List */}
      {interviews.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200/60 shadow-sm">
          <Calendar size={40} className="mx-auto text-slate-200 mb-3" />
          <p className="text-[15px] font-semibold text-slate-700">No interviews scheduled</p>
          <p className="text-[13px] text-slate-400 mt-1">Click "Schedule Interview" to create one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {interviews.map(interview => (
            <div key={interview.id} className="group bg-white rounded-2xl p-6 border border-slate-200/60 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-gray-900 text-lg">{interview.venue || 'TBD'}</h3>
                  <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-1">
                    <Briefcase size={14} /> {interview.campaign?.title}
                  </p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${statusColors[interview.status] || statusColors.scheduled}`}>
                  {interview.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                {interview.date && (
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <Calendar size={14} className="text-gray-400" /> {format(new Date(interview.date), 'PPP')}
                  </p>
                )}
                {interview.address && (
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <MapPin size={14} className="text-gray-400" /> {interview.address}
                  </p>
                )}
              </div>

              {interview.status === 'scheduled' && (
                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button onClick={() => openEditModal(interview)} className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-blue-600 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors">
                    <Edit2 size={14} /> Edit
                  </button>
                  <button onClick={() => handleStatusChange(interview.id, 'completed')} className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-green-600 px-3 py-1.5 rounded-lg hover:bg-green-50 transition-colors">
                    <CheckCircle size={14} /> Complete
                  </button>
                  <button onClick={() => handleStatusChange(interview.id, 'cancelled')} className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors">
                    <XCircle size={14} /> Cancel
                  </button>
                  <button onClick={() => handleDelete(interview.id)} className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors ml-auto">
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-black text-slate-900 tracking-tight">
                {editingInterview ? 'Edit Interview' : 'New Interview'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Campaign</label>
                <select name="campaign_id" value={formData.campaign_id} onChange={e => setFormData(p => ({ ...p, campaign_id: e.target.value }))} className={inputClass} required>
                  <option value="">Select campaign</option>
                  {campaigns.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Venue Name</label>
                  <input value={formData.venue} onChange={e => setFormData(p => ({ ...p, venue: e.target.value }))} placeholder="e.g. Dubai Office" className={inputClass} />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Date</label>
                  <input type="date" value={formData.date} onChange={e => setFormData(p => ({ ...p, date: e.target.value }))} className={inputClass} />
                </div>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Address</label>
                <input value={formData.address} onChange={e => setFormData(p => ({ ...p, address: e.target.value }))} placeholder="Full address" className={inputClass} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Latitude</label>
                  <input value={formData.location_lat} onChange={e => setFormData(p => ({ ...p, location_lat: e.target.value }))} placeholder="e.g. 25.2048" className={inputClass} />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Longitude</label>
                  <input value={formData.location_lng} onChange={e => setFormData(p => ({ ...p, location_lng: e.target.value }))} placeholder="e.g. 55.2708" className={inputClass} />
                </div>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Notes</label>
                <textarea value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Optional notes..." className={`${inputClass} resize-none`} />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="px-5 py-2.5 text-gray-500 font-bold hover:text-gray-800 transition-colors">Cancel</button>
              <button onClick={handleSave} className="flex items-center gap-2 bg-slate-950 text-white px-6 py-2.5 rounded-xl text-[13px] font-semibold hover:bg-slate-800 transition-all">
                <Save size={18} /> {editingInterview ? 'Save Changes' : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
